import { PPTDialogs } from '@/app/modules/ppt/services/ppt-dialogs.service';
import { selectPPTAccount } from '@/app/modules/ppt/state/entities/account/ppt-account.selectors';
import { selectPPTAppraisalInformation, selectPPTBillByAccountNumberAndTaxYear, selectPPTBillDelegateLink, selectPPTTaxInformation } from '@/app/modules/ppt/state/entities/bill/ppt-bill.selectors';
import { selectPPTParcel } from '@/app/modules/ppt/state/entities/parcel/ppt-parcel.selectors';
import { selectDelegatedBillLinkText, selectPPTBillsAvailability, selectPPTDisclaimer } from '@/app/modules/ppt/state/ppt-state/ppt-state.selectors';
import { billsForPPTAccountLoaded, billsForPPTAccountLoadFailed, loadBillsForPPTAccount } from '@/app/modules/ppt/state/ppt.actions';
import { selectState } from '@/app/state/app-state/app-state.selectors';
import { CurrencyPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ofType } from '@ces/sourced-action';
import { sync } from '@ces/sync';
import { isString } from '@egovsolutions/type-checking';
import { Actions } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { NullLists } from '@shared/services/null-lists.service';
import { Viewport } from '@shared/services/viewport.service';
import { State } from '@state/model';
import { EgovAPIErrorType, isEgovAPIError, PPTBillCertificateHolderContactInfo } from 'egov-api';
import { BehaviorSubject, combineLatest, filter, map, of, Subscription, switchMap } from 'rxjs';
import { PPTDelegated } from '../../services/ppt-delegated.service';
import { loadPPTState } from '../../state/ppt-state/ppt-state.actions';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'ppt-bill',
    templateUrl: './ppt-bill.component.html',
    styleUrls: ['./ppt-bill.component.scss'],
    encapsulation: ViewEncapsulation.None, // eslint-disable-line @angular-eslint/use-component-view-encapsulation
})
export class BillPage implements OnInit, OnDestroy // eslint-disable-line @angular-eslint/component-class-suffix
{
    constructor
    (
        // Dependencies

        private readonly route: ActivatedRoute,
        private readonly store: Store<State>,
        private readonly actions$: Actions,
        private readonly dialogsService: PPTDialogs,
        private readonly delegated: PPTDelegated,


        // Template dependencies

        protected readonly nullLists: NullLists,
        protected readonly viewport: Viewport,
    )
    {}


    // Initialization

    public ngOnInit()
    {
        this.store.dispatch(loadPPTState('BillPage'));
        this.initializeNavigationHandling();
    }


    // URL values

    protected readonly accountNumber$ = new BehaviorSubject<string | undefined>(undefined);
    protected readonly taxYear$ = new BehaviorSubject<number | undefined>(undefined);


    // Navigation

    private urlParamsSubscription?: Subscription;

    protected initializeNavigationHandling()
    {
        this.urlParamsSubscription = this.route.params.subscribe(params =>
        {
            if (params.accountNumber)
            {
                this.accountNumber$.next(params.accountNumber);
                this.taxYear$.next(params.taxYear ? +params.taxYear : undefined);

                this.loadBills();
            }
            else if (!this.accountNumber$.value)
                // If a value is already set, lack of value probably means navigating out
                throw new Error('Account number not passed.');
        });
    }


    // Load bill

    protected notFound = false;
    protected loadError = false;
    protected loadErrorRetry = false;
    protected loaded = false;

    private billsLoadedSubscription?: Subscription;
    private billsLoadFailedSubscription?: Subscription;

    private loadBillsCounter = Number.MIN_SAFE_INTEGER;

    private loadBills()
    {
        this.notFound = false;
        this.loadError = false;


        // Dispatch action

        const operationId = `BillComponent .loadBills() ${this.loadBillsCounter++}`;

        this.store.dispatch(loadBillsForPPTAccount('BillComponent .loadBills()', {
            operationId,
            accountNumber: this.accountNumber$.value!
        }));


        // Bills loaded or load error

        const billsLoaded$ = this.actions$.pipe(
            ofType(billsForPPTAccountLoaded),
            filter(action => action.operationId === operationId)
        );

        this.billsLoadedSubscription = billsLoaded$.subscribe(action =>
        {
            if (!this.taxYear$.value) this.taxYear$.next(action.latestYear);
            this.loaded = true;
        });


        // Bills load error

        const billsLoadFailed$ = this.actions$.pipe(
            ofType(billsForPPTAccountLoadFailed),
            filter(action => action.operationId === operationId)
        );

        this.billsLoadFailedSubscription = billsLoadFailed$.subscribe(({ error }) =>
        {
            if (isEgovAPIError(error))
            {
                if (error.type === EgovAPIErrorType.NOT_FOUND)
                    this.notFound = true;
                else
                {
                    this.loadError = true;
                    this.loadErrorRetry = !error.permanent;
                }
            }
            else
            {
                this.loadError = true;
                this.loadErrorRetry = true;
            }
        });
    }

    protected retry() { this.loadBills(); }


    // State / settings

    protected readonly state$ = this.store.select(selectState);

    protected readonly billsIsUnavailable$ = this.store.select(selectPPTBillsAvailability).pipe(
        map(a => a === undefined ? a : !a)
    );
    protected readonly disclaimers$ = combineLatest([this.store.select(selectPPTDisclaimer)]);


    // PPT Entities

    private readonly currencyPipe = new CurrencyPipe('en-US', 'USD');

    protected readonly bill$ = combineLatest([this.accountNumber$, this.taxYear$]).pipe(
        switchMap(([accountNumber, taxYear]) => accountNumber && taxYear
            ? this.store.select(selectPPTBillByAccountNumberAndTaxYear(accountNumber, taxYear)).pipe(
                map(bill => bill && {
                    ...bill,
                    projections: bill.projections?.map(p => ({
                        ...p,
                        amount: isString(p.amount) ? p.amount : this.currencyPipe.transform(p.amount)
                    })),
                })
            )
            : of(undefined)
        ),
    );

    protected readonly id$ = this.bill$.pipe(map(bill => bill?.id));

    protected readonly account$ = this.accountNumber$.pipe(
        switchMap(accountNumber => accountNumber
            ? this.store.pipe(select(selectPPTAccount(accountNumber)))
            : of(undefined)
        )
    );

    protected readonly parcel$ = this.bill$.pipe(
        switchMap(bill => bill
            ? this.store.select(selectPPTParcel(bill.parcelId))
            : of(undefined)
        ),
    );


    // Turned Over

    protected readonly delegated$ = this.id$.pipe(switchMap(id => this.delegated.billDelegated$(id)));
    protected readonly delegatedMessage$ = this.id$.pipe(switchMap(id => this.delegated.delegatedMessage$(id)));

    protected readonly delegateLink$ = this.id$.pipe(
        switchMap(billId => billId
            ? this.store.select(selectPPTBillDelegateLink(billId))
            : of(undefined)
        )
    );

    protected readonly delegateLinkText$ = this.delegated$.pipe(
        switchMap(delegated => delegated === undefined
            ? of(delegated)
            : delegated
                ? this.store.select(selectDelegatedBillLinkText)
                : of(null)
        )
    );


    // Charges

    protected readonly hasAnyChargesWhatsoever$ = this.bill$.pipe(
        map(bill =>
            !!bill?.charges
            &&
            (
                !!bill.charges.adValorem?.charges?.length
                ||
                !!bill.charges.nonAdValorem?.charges?.length
                ||
                !!bill.charges.feesAndInterests?.charges?.length
            )
        )
    );


    // Bill Information

    protected readonly appraisalInfo$ = this.id$.pipe(
        switchMap(id => id
            ? this.store.pipe(select(selectPPTAppraisalInformation(id)))
            : of(undefined)
        ),
    );

    protected readonly appraisalItems$ = this.appraisalInfo$.pipe(
        map(appraisalInfo =>
        {
            if (!appraisalInfo) return appraisalInfo;
            if (!appraisalInfo.totalPropertyValue) return null;

            const items: { name: string, amount: number }[] = [];

            if (appraisalInfo.landValue)
                items.push({ name: 'Land', amount: appraisalInfo.landValue });

            if (appraisalInfo.personalPropertyValue)
                items.push({ name: 'Personal Property', amount: appraisalInfo.personalPropertyValue });

            if (appraisalInfo.improvementValue)
                items.push({ name: 'Improvement', amount: appraisalInfo.improvementValue });

            if (items.length) return items;
            else return null;
        }),
    );

    protected readonly showAppraisalInfo$ = this.appraisalItems$.pipe(
        map(appraisalItems => appraisalItems === undefined ? undefined : !!appraisalItems?.length),
    );

    protected readonly taxInfo$ = this.id$.pipe(
        switchMap(id => id
            ? this.store.pipe(select(selectPPTTaxInformation(id)))
            : of(undefined)
        ),
    );

    protected readonly showTaxable$ = this.taxInfo$.pipe(
        map(taxInfo => taxInfo === undefined
            ? undefined
            : taxInfo?.assessedValue !== null
        ),
    );

    protected readonly showTaxLevy$ = this.taxInfo$.pipe(
        map(taxInfo => taxInfo === undefined
            ? undefined
            : (taxInfo?.assessedValue !== null || taxInfo?.taxRate !== null) && taxInfo?.taxLevy !== null
        ),
    );

    protected readonly balanceMovements$ = this.taxInfo$.pipe(
        map(taxInfo =>
        {
            if (!taxInfo) return taxInfo;

            const items: { name: string, amount: number, positive: boolean }[] = [];

            if (taxInfo.interest)
                items.push({ name: 'Interest', positive: true, amount: taxInfo.interest });

            if (taxInfo.fees)
                items.push({ name: 'Fees', positive: true, amount: taxInfo.fees });

            if (taxInfo.payments)
                items.push({ name: 'Payments', positive: false, amount: taxInfo.payments });

            if (taxInfo.stateRelief)
                items.push({ name: 'State Relief', positive: false, amount: taxInfo.stateRelief });

            if (taxInfo.countyRelief)
                items.push({ name: 'County Relief', positive: false, amount: taxInfo.countyRelief });

            if (items.length) return items;
            else return null;
        }),
    );


    // Certificate

    protected readonly certificateHasAddress$ = this.bill$.pipe(
        map(bill =>
            bill?.certificate
            &&
            !!(
                bill.certificate.holder.address1
                ||
                bill.certificate.holder.address2
                ||
                bill.certificate.holder.city
                ||
                bill.certificate.holder.state
                ||
                bill.certificate.holder.zip
            )
        )
    );

    protected asCertificateHolderContactInfo(value: any) { return value as PPTBillCertificateHolderContactInfo; }

    protected showContactBidderDialog(e?: Event)
    {
        if (e) e.preventDefault();

        this.dialogsService.showContactBidderDialog(sync(this.bill$)!.id);
    }


    // Clear subscriptions

    public ngOnDestroy()
    {
        if (this.billsLoadedSubscription) this.billsLoadedSubscription.unsubscribe();
        if (this.billsLoadFailedSubscription) this.billsLoadFailedSubscription.unsubscribe();
        this.urlParamsSubscription!.unsubscribe();
    }

}
